// How long before a competition starts its time stops being moveable.
//
// WHAT IT IS FOR
//
// An email goes out saying "starts Tuesday at 8pm", and then the date
// moves. Everyone told the old time has been told something false, and
// there is no way to un-send it. Inside the lock-in window the start time
// is fixed, so an announcement can be sent with confidence.
//
// WHAT IT ACTUALLY DOES, all three parts
//
//   1. A competition inside the window cannot be rescheduled.
//   2. It cannot be deleted either — cancelling is the most complete way
//      of breaking the promise the lock makes.
//   3. Nothing may be CREATED closer than the window, or it would be
//      locked the moment it existed.
//
// Everything else stays editable while locked: the answer, image, prize
// and price all change freely. Only the date is frozen, because the date
// is the only thing anyone was told.
//
// Demo competitions are exempt. Nobody announces a demo round.
import React, { useEffect, useState } from "react";
import { showToast } from "./toast.js";
import { siteDateTime } from "./siteTime.js";

const UNIT_MINUTES = { minutes: 1, hours: 60, days: 1440, weeks: 10080 };

// Shows a stored minute count in the largest unit that divides it evenly,
// so "90" reads as "90 minutes" but "10080" reads as "1 week".
function toValueUnit(totalMinutes) {
  const m = Math.round(Number(totalMinutes) || 0);
  if (m > 0 && m % 10080 === 0) return { value: m / 10080, unit: "weeks" };
  if (m > 0 && m % 1440 === 0) return { value: m / 1440, unit: "days" };
  if (m > 0 && m % 60 === 0) return { value: m / 60, unit: "hours" };
  return { value: m, unit: "minutes" };
}

// WHAT THE CHOICE COSTS IN DEPLOY TIME, computed from the settings in
// force rather than described in general.
//
// No update may be installed while a competition is active or LOCKED IN,
// so the moment an update can go in is the gap between one competition
// ending and the next one locking:
//
//     window = gap - lock-in
//
// Dead air is the enforced FLOOR on that gap - the scheduler applies
// Math.max(1, deadAirMinutes) - so this is the worst case rather than the
// only case. A site scheduling competitions hours apart has a wide window.
// A site running back to back at the floor has NONE once the lock-in
// reaches the dead air, and before 1.934.0 the only way to find that out
// was to try to deploy and be refused.
//
// Returns null for a lock-in of zero: no lock, nothing to warn about.
export function publishWindowCost(lockInMinutes, deadAirMinutes) {
  const lock = Math.max(0, Math.round(Number(lockInMinutes) || 0));
  if (lock <= 0) return null;
  const air = Math.max(1, Math.round(Number(deadAirMinutes) || 1));
  return { lock, air, left: air - lock };
}

function Frozen({ frozen, title, children }) {
  if (!frozen) return children;
  return (
    <fieldset disabled title={title} style={{ border: "none", padding: 0, margin: 0 }}>
      <div style={{ opacity: 0.55 }}>{children}</div>
    </fieldset>
  );
}

export default function LockInPanel({ token, wsRequest, canEdit, modeStatus, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [value, setValue] = useState(0);
  const [unit, setUnit] = useState("minutes");
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // DEMO ONLY, not "anything but Live".
  //
  // These are SETTINGS, and freezing them in Test mode meant switching to
  // Live to save a form and switching back to carry on testing — friction
  // with little behind it. Provider credentials already have separate
  // test and live slots, and the data on screen is gated by mode anyway.
  //
  // Demo stays frozen: root pulls it as a brake to show somebody the
  // product, and settings changing under a demonstration is a real
  // surprise. The server enforces the same rule in
  // assertSettingsEditable.
  const frozen = (modeStatus?.effectiveMode || "live") === "demo";
  const frozenTitle = "Leave Demo mode to manage this";
  const locked = !canEdit || frozen;

  const load = () => {
    // Its OWN action, on manage_competitions — the permission this panel
    // is gated by. admin.siteSettings.private requires
    // manage_payment_providers, which has nothing to do with scheduling.
    wsRequest("admin.lockIn.get", {}, token)
      .then((d) => {
        setSettings(d);
        const parsed = toValueUnit(d.competitionLockInMinutes || 0);
        setValue(parsed.value);
        setUnit(parsed.unit);
      })
      .catch((err) => {
        setLoadFailed(true);
        showToast(err.message || "Could not reach backend.", "error");
      });
  };

  // RELOADS WHEN THE MODE CHANGES, not just on mount. This panel shows
  // the next free slot, which is computed for the acting admin's mode — switching
  // mode changed the answer and left the old one on screen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [modeStatus?.effectiveMode]);

  const save = async () => {
    setBusy(true);
    try {
      const minutes = Math.round(Number(value) || 0) * UNIT_MINUTES[unit];
      await wsRequest("admin.siteSettings.update", { competitionLockInMinutes: minutes }, token);
      showToast(minutes === 0 ? "Lock-in turned off." : "Saved.");
      load();
      // TELL WHOEVER RENDERED THIS.
      //
      // The promotion panel beside this one enables or disables its
      // "when a competition is confirmed" checkbox based on the lock —
      // and had no way to learn the lock had changed, so it needed a page
      // refresh to catch up. Saving site settings broadcasts nothing, and
      // adding a broadcast for one card would be more machinery than a
      // callback between two panels that already share a parent.
      if (onSaved) onSaved();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    } finally {
      setBusy(false);
    }
  };

  // loadFailed, not the old `error` state: the message itself now goes to
  // a toast, but this still has to stop showing "Loading…" for ever when
  // the fetch fails.
  if (!settings && !loadFailed) return <p>Loading…</p>;

  const current = settings?.competitionLockInMinutes || 0;
  const schedulable = settings?.earliestSchedulable || null;
  const cost = publishWindowCost(
    Math.round(Number(value) || 0) * UNIT_MINUTES[unit],
    settings?.deadAirMinutes
  );

  // A CARD IN THE SETTINGS GRID, not a full-width block below it.
  //
  // No maxWidth and no top margin: the grid decides the width, and on a
  // narrow screen that is the whole column. A fixed 640 here would have
  // stopped the card shrinking and broken the layout on a phone.
  return (
    <section
      style={{
        background: "var(--bg-panel, rgba(127,127,127,0.04))",
        border: "1px solid var(--border-color)",
        borderRadius: 10,
        padding: "14px 16px",
      }}
    >
      <Frozen frozen={frozen} title={frozenTitle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Competition lock-in</div>
        <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>
          How long before a competition starts its date stops being moveable — so an email
          announcing it can state a time you know will hold.
        </p>


        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="number"
            min="0"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={locked || busy}
            title={frozen ? frozenTitle : undefined}
            style={{ width: 90 }}
          />
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={locked || busy}
            title={frozen ? frozenTitle : undefined}
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
            <option value="weeks">weeks</option>
          </select>
          <button type="button" onClick={save} disabled={locked || busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>

        <p style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>Zero turns it off entirely.</p>

        {/* FROM THE TYPED VALUE, not the saved one. The point is to answer
            the question before Save, which is the moment the decision is
            actually made. */}
        {cost && (
          <div
            data-publish-window={cost.left > 0 ? "some" : "none"}
            style={{
              marginTop: 10,
              padding: "9px 11px",
              borderRadius: 8,
              fontSize: 12.5,
              lineHeight: 1.5,
              border: "1px solid var(--border-color)",
              background: cost.left > 0 ? "transparent" : "var(--bg-panel, rgba(127,127,127,0.04))",
            }}
          >
            <strong>Updates:</strong>{" "}
            {cost.left > 0 ? (
              <>
                an update can only be installed between one competition ending and the next
                locking in. With dead air at {cost.air} minute{cost.air === 1 ? "" : "s"}, a
                lock-in of {cost.lock} leaves <strong>{cost.left} minute
                {cost.left === 1 ? "" : "s"}</strong> for that on a back-to-back queue. Wider if
                you schedule competitions further apart.
              </>
            ) : (
              <>
                on a back-to-back queue this leaves <strong>no window at all</strong> — the next
                competition locks in before the last one ends, so installing an update would mean
                stopping the queue. Dead air is {cost.air} minute
                {cost.air === 1 ? "" : "s"}; raising it above {cost.lock} is what buys a window
                back. Scheduling competitions further apart does the same.
              </>
            )}
          </div>
        )}

        {current > 0 && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              border: "1px solid var(--border-color)",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            <strong>While a competition is inside its window:</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              <li>its start time cannot be changed</li>
              <li>it cannot be deleted</li>
              <li>
                everything else stays editable — answer, image, prize, price. Only the date is
                frozen, because the date is the only thing anyone was told.
              </li>

            </ul>
            {/* THE REAL EARLIEST, and why. The lock is only one of two
                constraints: a queue five deep pushes the answer much
                later, and "you cannot, because X runs until somebody
                wins" is a third case entirely. Saying which one is
                biting is the difference between a date somebody
                understands and one they argue with. */}
            {schedulable?.reason === "open-ended" ? (
              <p style={{ marginBottom: 0, marginTop: 8 }}>
                Nothing can be scheduled at the moment:{" "}
                <strong>{schedulable.blockedBy?.title || "a competition"}</strong> has no
                scheduled end — it runs until somebody wins, so there is no "after" to book.
              </p>
            ) : schedulable?.at ? (
              <p style={{ marginBottom: 0, marginTop: 8 }}>
                {/* ADVICE, not a rule. The lock floor IS enforced; the
                    queue part is a suggestion, because nothing currently
                    stops a manual reschedule overlapping — see TODO.md.
                    Saying "would be free" rather than "can" keeps that
                    honest. */}
                The next free slot is{" "}
                <strong>{siteDateTime(schedulable.at)}</strong>
                {schedulable.reason === "queue" && schedulable.blockedBy ? (
                  <> — that is when {schedulable.blockedBy.title} finishes.</>
                ) : (
                  <> — the lock-in period from now.</>
                )}
              </p>
            ) : null}
            <p style={{ marginBottom: 0, marginTop: 8, opacity: 0.75 }}>
              Demo competitions are exempt — nobody announces a demo round.
            </p>
          </div>
        )}
      </Frozen>
    </section>
  );
}
